import { Button, Icon } from "@legal-500/legal500-design-system";
import { useEffect, useRef } from "react";
import type { CrmPackageMapping } from "../../api/crm-package-mappings.service.js";
import styles from "./CrmPackageMappingDeleteModal.module.css";

export type CrmPackageMappingDeleteModalProps = {
  mapping: CrmPackageMapping | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting?: boolean;
};

export function CrmPackageMappingDeleteModal({
  mapping,
  isOpen,
  onClose,
  onConfirm,
  isDeleting = false,
}: CrmPackageMappingDeleteModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && !isDeleting) {
        onClose();
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, isDeleting, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocusedElement = document.activeElement as HTMLElement;

    const modal = modalRef.current;
    if (modal) {
      const focusable = modal.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable) {
        (focusable as HTMLElement).focus();
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Tab" && modal) {
        const focusableElements = modal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }

    modal?.addEventListener("keydown", handleKeyDown);

    return () => {
      modal?.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedElement?.focus();
    };
  }, [isOpen]);

  if (!isOpen || !mapping) return null;

  function handleBackdropClick(event: React.MouseEvent) {
    if (event.target === event.currentTarget && !isDeleting) {
      onClose();
    }
  }

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className={styles.modalContainer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-mapping-title"
        aria-describedby="delete-mapping-message"
      >
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <div className={styles.warningIcon} aria-hidden="true">
              <Icon icon="exclamation" size="large" />
            </div>
            <h2 id="delete-mapping-title" className={styles.modalTitle}>
              Delete Mapping
            </h2>
          </div>
          <button
            className={styles.modalCloseButton}
            onClick={onClose}
            disabled={isDeleting}
            aria-label="Close modal"
          >
            <Icon icon="cross" size="small" />
          </button>
        </div>

        <div className={styles.modalBody}>
          <p id="delete-mapping-message" className={styles.deleteMessage}>
            Are you sure you want to delete this CRM package mapping? This action cannot be undone.
          </p>

          <div className={styles.mappingInfo}>
            <div className={styles.mappingInfoRow}>
              <span className={styles.mappingInfoLabel}>CRM Package ID:</span>
              <span className={styles.mappingInfoValue}>{mapping.crmPackageId}</span>
            </div>
            <div className={styles.mappingInfoRow}>
              <span className={styles.mappingInfoLabel}>Package:</span>
              <span className={styles.mappingInfoValue}>
                {mapping.package
                  ? `${mapping.package.name} (${mapping.package.productCode})`
                  : "— (skip)"}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <Button variant="outlined" color="default" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="default"
            onClick={onConfirm}
            disabled={isDeleting}
            className={styles.dangerButton}
          >
            {isDeleting ? (
              <>
                <span className={styles.spinner} />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
